import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import Global from '../../store/global-selectors';
import { getEnhancedReturnsCountForStore, getEnhancedReturnsCounts, getEnhancedReturnsSellerCounts } from '../../store/returns/returns.api';
import {
  ALL_STORES_STORE_GUID,
  STATUS_AWAITING_SHIPMENT,
  STATUS_COMPLETED,
  STATUS_PENDING_PAYMENT,
  STATUS_PENDING_APPROVAL,
  STATUS_PROCESSING,
  STATUS_FLAGGED,
  NEEDS_ATTENTION,
  useIsUserAbleToUseEnhancedReturns,
  useEnhancedReturnRouteMatch,
} from '../../store/returns/returns.helpers';
import { useIsShipstationReturnsEnabled } from '../../hooks/use-seller-setting';

export const EnhancedReturnsCountContext = createContext();
export const useEnhancedReturnsCountContext = () => React.useContext(EnhancedReturnsCountContext);

const parseStatusCounts = (result) => {
  const statusViewCountMap = {
    [STATUS_PENDING_APPROVAL]: result.pendingApproval ?? 0,
    [STATUS_PENDING_PAYMENT]: result.pendingPayment ?? 0,
    [STATUS_AWAITING_SHIPMENT]: result.awaitingShipment ?? 0,
    [STATUS_PROCESSING]: result.processing ?? 0,
    [STATUS_COMPLETED]: result.completed ?? 0,
    [NEEDS_ATTENTION]: result.needsAttention ?? 0,
    [STATUS_FLAGGED]: result.needsAttention ?? 0,
  };

  const totalCount = result.totalCount ?? 0;

  return { totalCount, statusViewCountMap };
};

export const EnhancedReturnsCountContextProvider = ({ children }) => {
  const [countsPerStoreMap, setCountsPerStoreMap] = useAsyncSafeState({});
  const [isSearchMode, setIsSearchMode] = useAsyncSafeState(false);
  const [searchTerm, setSearchTerm] = useAsyncSafeState('');
  const userCanUseEnhancedReturns = useIsUserAbleToUseEnhancedReturns();
  const { storeGuid: currentEnhancedReturnStoreGuid } = useEnhancedReturnRouteMatch();

  const stores = useSelector(Global.selectSellerActiveStores);
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const enhancedReturnsEnabledStoreGuids = useMemo(
    // When SSR is enabled, all active stores are treated as enhanced returns enabled
    () =>
      isShipstationReturnsEnabled
        ? stores.map((s) => s.storeGuid)
        : stores.filter((s) => s.isStoreSetupCompleteForEnhancedReturns).map((s) => s.storeGuid),
    [isShipstationReturnsEnabled, stores],
  );

  const fetchCountForStore = useCallback(
    async (storeGuid, term, filteredStoreGuids = null) => {
      if (!userCanUseEnhancedReturns) {
        return;
      }

      const isAllStores = storeGuid === ALL_STORES_STORE_GUID;

      const result = isAllStores
        ? await getEnhancedReturnsSellerCounts(term, filteredStoreGuids ?? [])
        : await getEnhancedReturnsCountForStore(storeGuid, term);
      const { totalCount, statusViewCountMap } = parseStatusCounts(result);

      setCountsPerStoreMap((prev) => ({
        ...prev,
        [storeGuid]: { totalCount, statusViewCountMap },
      }));
    },
    [setCountsPerStoreMap, userCanUseEnhancedReturns],
  );

  const fetchAllStoreCountsExceptCurrent = useCallback(
    async (term) => {
      if (!userCanUseEnhancedReturns) {
        return;
      }
      try {
        const storeGuids = enhancedReturnsEnabledStoreGuids.filter((guid) => guid !== currentEnhancedReturnStoreGuid);
        const allStoreResults = await getEnhancedReturnsCounts(storeGuids, term);

        const resultsMap = allStoreResults.reduce((acc, result) => {
          const { storeGuid } = result;
          const { totalCount, statusViewCountMap } = parseStatusCounts(result);
          return {
            ...acc,
            [storeGuid]: { totalCount, statusViewCountMap },
          };
        }, {});

        setCountsPerStoreMap((prev) => ({
          ...prev,
          ...resultsMap,
        }));
      } catch {
        // leave existing counts intact; individual store counts remain valid
      }
    },
    [currentEnhancedReturnStoreGuid, enhancedReturnsEnabledStoreGuids, setCountsPerStoreMap, userCanUseEnhancedReturns],
  );

  const onSearch = useCallback(
    (term) => {
      setIsSearchMode(true);
      setCountsPerStoreMap({});
      setSearchTerm(term);
      fetchAllStoreCountsExceptCurrent(term);
    },
    [fetchAllStoreCountsExceptCurrent, setCountsPerStoreMap, setIsSearchMode, setSearchTerm],
  );

  const onClearSearchMode = useCallback(() => {
    setIsSearchMode(false);
    setCountsPerStoreMap({});
    setSearchTerm('');
    fetchAllStoreCountsExceptCurrent('');
  }, [fetchAllStoreCountsExceptCurrent, setCountsPerStoreMap, setIsSearchMode, setSearchTerm]);

  useEffect(() => {
    if (enhancedReturnsEnabledStoreGuids.length > 0) {
      fetchAllStoreCountsExceptCurrent(searchTerm);
    }
    // Re-runs when store list becomes non-empty (e.g. stores load after mount)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [enhancedReturnsEnabledStoreGuids.length]);

  const value = useMemo(
    () => ({
      countsPerStoreMap,
      fetchCountForStore,
      isSearchMode,
      onClearSearchMode,
      onSearch,
      searchTerm,
    }),
    [countsPerStoreMap, fetchCountForStore, isSearchMode, onClearSearchMode, onSearch, searchTerm],
  );

  return <EnhancedReturnsCountContext.Provider value={value}>{children}</EnhancedReturnsCountContext.Provider>;
};
